<template>
  <div class="UILearnMoreCards">
    <div class="UILearnMoreCards__cards">
      <div
        class="UILearnMoreCards__card"
        v-for="card in cards"
        :key="card.title"
        :style="{
          '--card-gradient-to': card.gradientTo,
          '--card-background-color': card.backgroundColor,
          backgroundImage: `linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, var(--card-gradient-to, rgba(255, 255, 255, 0.95)) 50%), url(${card.image})`,
        }"
      >
        <h3 class="UILearnMoreCards__card-title">{{ card.title }}</h3>
        <p class="UILearnMoreCards__card-description">
          {{ card.description }}
        </p>
        <a
          v-if="card.link"
          :href="card.link"
          class="UILearnMoreCards__card-button"
        >
          {{ buttonText }}
          <UIIcon name="arrowLink" class="UILearnMoreCards__card-button-icon" />
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PropType } from 'vue'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'

export interface LearnMoreCardItem {
  image: string
  title: string
  description: string
  link?: string
  /** Optional gradient overlay destination color */
  gradientTo?: string
  /** Optional background color fallback */
  backgroundColor?: string
}

defineProps({
  cards: {
    type: Array as PropType<LearnMoreCardItem[]>,
    required: true,
  },
  buttonText: {
    type: String,
    default: 'Learn more',
  },
})
</script>

<style lang="scss" scoped>
.UILearnMoreCards {
  --card-background-color: #{$color-white};

  .UILearnMoreCards__cards {
    margin-top: 0; // let parent control spacing
    display: grid;
    gap: 1.5rem;
    // Responsive grid that wraps when a card would be < 18rem
    grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));

    @include sm {
      gap: 2rem;
    }

    @include md {
      gap: 2.5rem;
    }

    @include lg {
      gap: 2.75rem;
    }
  }

  .UILearnMoreCards__card {
    background-size: cover, contain;
    background-repeat: no-repeat, no-repeat;
    background-position:
      center,
      center top;
    background-color: var(--card-background-color);
    border-radius: 1rem;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    box-shadow:
      0 1px 3px 0
        var(--Colors-Effects-Shadows-shadow-sm_01, rgba(0, 16, 53, 0.1)),
      0 1px 2px -1px
        var(--Colors-Effects-Shadows-shadow-sm_02, rgba(0, 16, 53, 0.1));
    // Maintain aspect ratio of 795x968 (0.821)
    // Height = width / 0.821 = width * 1.22
    aspect-ratio: 795 / 968;
    min-height: 280px; // Minimum height for mobile

    @include sm {
      min-height: 320px;
    }

    @include md {
      min-height: 360px;
    }

    @include lg {
      min-height: 400px;
    }

    @include xl {
      min-height: 450px;
    }
  }

  .UILearnMoreCards__card-title {
    @include font-size-24px();
    font-weight: 600;
    color: $color-ui-gray-cool-900;
    margin-bottom: 1rem;

    @include sm {
      margin-bottom: 1.125rem;
    }

    @include md {
      margin-bottom: 1.25rem;
    }
  }

  .UILearnMoreCards__card-description {
    @include font-size-16px();
    color: $color-ui-gray-cool-600;
    margin-bottom: 1rem;
    line-height: 1.5;

    @include sm {
      margin-bottom: 1.125rem;
    }

    @include md {
      margin-bottom: 1.25rem;
    }
  }

  .UILearnMoreCards__card-button {
    @include font-size-14px();
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: $color-ui-gray-cool-600;
    font-weight: 600;
    transition: color 0.2s ease;

    &:hover {
      color: $color-ui-gray-cool-900;
    }

    @include sm {
      @include font-size-14px();
    }

    @include md {
      @include font-size-16px();
    }
  }

  .UILearnMoreCards__card-button-icon {
    --color: #{$color-ui-gray-cool-600};
    transition: --color 0.2s ease;
  }

  .UILearnMoreCards__card-button:hover .UILearnMoreCards__card-button-icon {
    --color: #{$color-ui-gray-cool-900};
  }
}
</style>
